import React, { useEffect } from 'react';
import moment from 'moment-timezone';
import {
  Form,
  FormInstance,
  DateSerialized,
  SelectBooleanInput,
  RadioGroup,
  FormErrors,
  FormData
} from 'blueprint-form';

import { Row, Col } from '../../../../grid';
import { StartDate } from '../date';
import { EndDate } from '../date';
import SelectMethod from '../method';
import SelectTime from '../time';
import Weekday from '../weekday';
import Timezone from '../timezone';
import { CardBlock, CardBreakout } from '../../card';
import Callout from '../../callout';
import { GetCurrentTZ } from '../../../../libs/time';
import { useTerms } from '../../../../contexts/terms';

type Mode = 'W';

const GetNTimesFromMode = (mode: Mode): number => {
  const nTimes = 1;
  switch (mode) {
    case 'W':
  }
  return nTimes;
};

interface EpxAsthmaYZData {
  method: string;
  language: string;
  start: string;
  end?: string;
  tz: string;
  isFixed: boolean;
  mode?: Mode;
  times: string[];
  days: number[];
  step?: number;
  shouldRecheck: boolean;
  checkYellowZone: boolean;
  shouldAskACT: boolean;
}

type EpxAsthmaYZDataRaw = DateSerialized<EpxAsthmaYZData, 'start' | 'end'>;

const DATE_FORMAT = 'YYYY-MM-DD';
const { DeserializeDate, SerializeDate } = FormInstance;

interface EpxAsthmaYZFormProps {
  setForm?: (form: FormInstance<EpxAsthmaYZDataRaw>) => void;
  onSubmit?: (data: EpxAsthmaYZData) => void;
}

const EpxAsthmaYZForm = ({
  setForm,
  onSubmit: _onSubmit
}: EpxAsthmaYZFormProps): JSX.Element => {
  const { terms } = useTerms();
  const serialize = (data: EpxAsthmaYZDataRaw): EpxAsthmaYZData => {
    return {
      ...data,
      start: SerializeDate(data.start, DATE_FORMAT),
      end: SerializeDate(data.end, DATE_FORMAT),
      times: data.times.slice(0, GetNTimesFromMode(data.mode))
    };
  };
  const deserialize = (data: EpxAsthmaYZData): EpxAsthmaYZDataRaw => {
    return {
      ...data,
      start: DeserializeDate(data.start, DATE_FORMAT),
      end: DeserializeDate(data.end, DATE_FORMAT)
    };
  };
  const initialTimes = ['12:00'];
  const form = new FormInstance<EpxAsthmaYZDataRaw, EpxAsthmaYZData>(
    deserialize({
      method: 'message',
      language: 'en',
      start: moment().format(DATE_FORMAT),
      end: null,
      tz: GetCurrentTZ(),
      isFixed: true,
      mode: 'W',
      step: 1,
      times: initialTimes,
      days: [1, 3],
      shouldRecheck: true,
      checkYellowZone: true,
      shouldAskACT: true
    }),
    { serialize, deserialize }
  );
  useEffect(() => {
    if (setForm) {
      setForm(form);
    }
  }, []);
  const onSubmit = (data: EpxAsthmaYZData): void => {
    _onSubmit(data);
  };
  // Provide form level validation (e.g. validation on 2+ values)
  const validate = (values: FormData): FormErrors => {
    const errors: FormErrors = {};
    // Need to copy the array before sorting because react props are immutable
    const days = values.days.slice().sort();
    const daysOfWeek = [0, 1, 2, 3, 4, 5, 6];
    let isValid = true;
    // Handle nonconsecutive
    for (let i = 0; i < days.length; i++) {
      const currentDayIndex = daysOfWeek.indexOf(days[i]);
      const nextDayIndex = daysOfWeek.indexOf(days[i + 1]);
      if (nextDayIndex - currentDayIndex === 1) {
        isValid = false;
      }
    }
    // Handle when Sat and Sun are picked
    if (days.includes(0) && days.includes(6)) {
      isValid = false;
    }
    if (!isValid) {
      errors.days = 'The days must be nonconsecutive!';
    }
    return errors;
  };
  return (
    <CardBlock bare>
      <Form form={form} onSubmit={onSubmit} validate={validate}>
        {() => {
          return (
            <>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <SelectMethod isMessageOnly />
                  </Col>
                  <Col sm={12}>
                    <RadioGroup
                      label="Language"
                      name="language"
                      options={[
                        {
                          label: 'English',
                          value: 'en'
                        }
                      ]}
                      inline
                    />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <StartDate label="Start Date" name="start" fill />
                  </Col>
                  <Col sm={12}>
                    <EndDate label="End Date" name="end" />
                  </Col>
                  <Col sm={12}>
                    <SelectTime label="Message At" name="times[0]" />
                  </Col>
                  <Col sm={12}>
                    <Timezone label="Timezone" name="tz" fill />
                  </Col>
                </Row>
              </CardBlock>
              <CardBreakout>
                <Row>
                  <Col sm={24}>
                    <Weekday
                      name="days"
                      minItems={1}
                      maxItems={3}
                      inline
                      abbreviated
                    />
                  </Col>
                </Row>
              </CardBreakout>
              <CardBlock>
                <Row>
                  <Col sm={24}>
                    <SelectBooleanInput
                      label="Ask Asthma Control Test (ACT)"
                      name="shouldAskACT"
                      fill
                    />
                    <Callout>
                      Saying 'Yes' to this means the {terms.patient.default}{' '}
                      will receive an ACT survey if they respond no to having
                      any symptoms, and they have had no alerts in the past 4
                      weeks.
                    </Callout>
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={24}>
                    <SelectBooleanInput
                      label="Ask Yellow Zone Plan Review"
                      name="checkYellowZone"
                      fill
                    />
                    <Callout>
                      Saying 'Yes' to this means the {terms.patient.default}{' '}
                      will be asked about reviewing their asthma plan before
                      beginning their survey for the day.
                    </Callout>
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={24}>
                    <SelectBooleanInput
                      label="Enable 24 Hour Recheck"
                      name="shouldRecheck"
                      fill
                    />
                    <Callout>
                      Saying 'Yes' to this means the {terms.patient.default}{' '}
                      will receive a 24hr follow-up asking if their asthma
                      symptoms are in the Green Zone.
                    </Callout>
                  </Col>
                </Row>
              </CardBlock>
            </>
          );
        }}
      </Form>
    </CardBlock>
  );
};

export default EpxAsthmaYZForm;
